import { Component, DestroyRef, OnInit, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";
import { ConfirmDialogComponent } from "../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetMemberService } from "../../../data/member/get-member.service";
import { UpdateMemberService } from "../../../data/member/update-member.service";

import dayjs from "dayjs";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { STAFF_ROLES_I18N } from "./staff-roles.i18n";

// Mode enum for dialog states
export enum StaffRolesMode {
  disp = 'disp',
  edit = 'edit',
}

@Component({
  selector: "app-staff-roles",
  templateUrl: "./staff-roles.component.html",
  styleUrls: ["./staff-roles.component.scss"],
  imports: SHARED_IMPORTS,
})
export class StaffRolesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = STAFF_ROLES_I18N;

  // Mode management - starts in display mode
  Mode = StaffRolesMode;
  mode = StaffRolesMode.disp;

  // Loading state for skeleton - true initially to show skeleton until data is ready
  isLoading = signal(true);
  isSending = signal(false);
  private savedSuccessfully = false;

  public validId: boolean = false;
  public primaryAccount: boolean = false;
  private ownAccount: boolean = false;
  public accountData: AccountData;

  private readonly defaultAccountData: AccountData = {
    user: {
      userId: "",
      lastName: "",
      firstName: "",
    },
    permissions: {
      userType: "",
      role: [],
      privileges: [],
    },
    contractInfo: {
      contractStatus: "",
      contractStartAt: "--/--/--",
      contractEndAt: "--/--/--",
    },
    commonInfo: {
      lastModifiedAt: "--/--/--",
      lastModifiedBy: "--",
    },
    id: "",
  };

  updateAccountData: AccountData;
  putParam;

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];

  accountGroup = [
    { value: "STAFF", viewValue: this.i18n.accountGroup.staff },
    { value: "USER", viewValue: this.i18n.accountGroup.user },
  ];

  selectedStatus = "";

  selectedGroup = "";
  selectedRole = [];
  selectedPrivileges = [];

  accountManageRole = false;
  userManageRole = false;
  measureRole = false;
  referRole = false;

  memberId: string;
  sectionId: string;
  loginUserId: string;

  constructor(
    public dialogRef: MatDialogRef<StaffRolesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    private getMemberService: GetMemberService,
    private updateMemberService: UpdateMemberService,
    private router: Router,
    public snackBar: MatSnackBar,
    public _matdialog: MatDialog,
    private common: DatabaseCacheService,
  ) {
    this.resetFields();
    this.accountData = this.defaultAccountData;
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.memberId = data?.id;

      let user = this.msalService.instance.getActiveAccount();
      this.loginUserId = user.idTokenClaims["sub"];

      if (this.memberId === this.loginUserId) {
        this.ownAccount = true;
      }

      this.sectionId = this.common.getSelectedSection().sectionId;
      this.getMember(this.sectionId, this.memberId);
    });
  }

  ngOnInit() {}

  update() {

    if (this.ownAccount) {

      if (!this.accountManageRole || this.selectedStatus === "CLOSE") {
        const dialogRef = this._matdialog.open(ConfirmDialogComponent, {
          disableClose: false,
          data: {
            title: this.i18n.dialog.deleteTitle,
            message: this.i18n.dialog.deleteMessage,
          },
        });

        dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {

          if (result) {
            this.updateMember();
          }
        });
      } else {
        this.updateMember();
      }
    } else {
      this.updateMember();
    }
  }

  submit() {
  }

  updateMember() {

    this.selectedRole = [];
    this.selectedPrivileges = [];

    if (this.primaryAccount) {
      this.selectedRole.push("PRIMARY");
    }

    if (this.accountManageRole) {
      this.selectedRole.push("ACCOUNT_MANAGEMENT");
      this.selectedPrivileges.push(
        AccountManagementPrivileges.CREATE_ACCOUNT,
        AccountManagementPrivileges.READ_ACCOUNT,
        AccountManagementPrivileges.UPDATE_ACCOUNT,
        AccountManagementPrivileges.DELETE_ACCOUNT,
      );
    }

    if (this.userManageRole) {
      this.selectedRole.push("USER_MANAGEMENT");
      this.selectedPrivileges.push(
        UserManagementPrivileges.CREATE_USER,
        UserManagementPrivileges.READ_USER,
        UserManagementPrivileges.UPDATE_USER,
        UserManagementPrivileges.DELETE_USER,
      );
    }

    if (this.measureRole) {
      this.selectedRole.push("MEASUREMENT");
      this.selectedPrivileges.push(
        MeasurementPrivileges.READ_USER,

        MeasurementPrivileges.CREATE_RECORD,
        MeasurementPrivileges.READ_RECORD,
        MeasurementPrivileges.UPDATE_RECORD,
        MeasurementPrivileges.DELETE_RECORD,

        MeasurementPrivileges.CREATE_FOLLOWUP,
        MeasurementPrivileges.READ_FOLLOWUP,
        MeasurementPrivileges.UPDATE_FOLLOWUP,
        MeasurementPrivileges.DELETE_FOLLOWUP,
      );
    }

    if (this.referRole) {
      this.selectedRole.push("REFER");
      this.selectedPrivileges.push(
        ReferePrivileges.READ_USER,
        ReferePrivileges.READ_RECORD,
        ReferePrivileges.READ_FOLLOWUP,
      );
    }

    this.putParam = {
      userType: this.selectedGroup,
      role: this.selectedRole,
      privileges: this.selectedPrivileges,
      contractStatus: this.selectedStatus,
    };

    this.isSending.set(true);
    this.updateMemberService
      .updateMember(this.memberId, this.putParam)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe(
        (res) => {

          if (res == null) {
            // this.snackBar.open("ERROR: invalid ID", null, {
            //   duration: 5000,
            // });
          } else {
            if (this.ownAccount) {
              let roles: string[] = (res as any).permissions.role;
              this.common.setSelectedSectionRoles(roles);
            }
          }

          this.snackBar.open(this.i18n.message.updateSuccess, this.i18n.button.close, { duration: 3000 });

          if (this.ownAccount && !this.accountManageRole && !this.userManageRole) {
            this.router.navigate(["/home"]);
            return;
          }
          if (this.ownAccount && !this.accountManageRole && this.userManageRole) {
            this.router.navigate(["/section-admin/home"]);
            return;
          }

          this.savedSuccessfully = true;
          this.mode = StaffRolesMode.disp;
          this.getMember(this.sectionId, this.memberId);
        },

        (error) => {
          this.snackBar.open(
            this.i18n.message.updateError,
            this.i18n.button.close,
            {
              duration: 5000,
            },
          );
        },
      );
  }

  getMember(sectionId: string, memberId: string) {
    this.isLoading.set(true);
    this.resetFields();

    this.getMemberService.getMember(sectionId, memberId, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          let convertedRes = res as Array<any>;
          if (convertedRes.length == 0) {
            this.snackBar.open(
              this.i18n.message.invalidMemberId,
              null,
              {
                duration: 5000,
              },
            );
          } else {
            let userId = convertedRes[0].user.userId;

            if (userId === this.loginUserId) {
              this.ownAccount = true;
            }

            let roles: string[] = convertedRes[0].permissions.role;

            if (roles.indexOf("PRIMARY") !== -1) {
              this.primaryAccount = true;
            }

            this.accountData = {
              user: {
                userId: userId,
                lastName: convertedRes[0].user.lastName,
                firstName: convertedRes[0].user.firstName,
              },
              permissions: {
                userType: convertedRes[0].permissions.userType,
                role: roles,
                privileges: convertedRes[0].permissions.privileges,
              },
              contractInfo: {
                contractStatus:
                  convertedRes[0].contractInfo.contractStatus.toUpperCase(),
                contractStartAt: dayjs(
                  convertedRes[0].contractInfo.contractStartAt,
                ).isValid()
                  ? dayjs(convertedRes[0].contractInfo.contractStartAt).format(
                      "YYYY/MM/DD",
                    )
                  : "--/--/--",
                contractEndAt: dayjs(
                  convertedRes[0].contractInfo.contractEndAt,
                ).isValid()
                  ? dayjs(convertedRes[0].contractInfo.contractEndAt).format(
                      "YYYY/MM/DD",
                    )
                  : "--/--/--",
              },
              commonInfo: {
                lastModifiedAt: convertedRes[0].commonInfo.lastModifiedAt,
                lastModifiedBy: convertedRes[0].commonInfo.lastModifiedBy,
              },
              id: convertedRes[0].id,
            };

            this.selectedStatus =
              convertedRes[0].contractInfo.contractStatus.toUpperCase();
            this.selectedGroup =
              convertedRes[0].permissions.userType.toUpperCase();

            if (
              convertedRes[0].permissions.role
                ? convertedRes[0].permissions.role.indexOf(
                    "ACCOUNT_MANAGEMENT",
                  ) !== -1
                : false
            ) {
              this.accountManageRole = true;
            }
            if (
              convertedRes[0].permissions.role
                ? convertedRes[0].permissions.role.indexOf("USER_MANAGEMENT") !==
                  -1
                : false
            ) {
              this.userManageRole = true;
            }
            if (
              convertedRes[0].permissions.role
                ? convertedRes[0].permissions.role.indexOf("MEASUREMENT") !== -1
                : false
            ) {
              this.measureRole = true;
            }
            if (
              convertedRes[0].permissions.role
                ? convertedRes[0].permissions.role.indexOf("REFER") !== -1
                : false
            ) {
              this.referRole = true;
            }
            this.validId = true;
          }
        },
        error: () => {
          // finalize handles isLoading.set(false)
        },
      });
  }

  onNoClick(): void {
    this.dialogRef.close({ needRefresh: this.savedSuccessfully });
  }

  resetFields(): void {
    this.validId = false;
    this.selectedStatus = "";
    this.accountManageRole = false;
    this.userManageRole = false;
    this.measureRole = false;
    this.referRole = false;
    this.accountData = this.defaultAccountData;
  }
}

export interface AccountData {
  user: {
    userId: string;
    lastName: string;
    firstName: string;
  };
  permissions: {
    userType: string;
    role: string[];
    privileges: KeyValue[];
  };
  contractInfo: {
    contractStatus: string;
    contractStartAt: string;
    contractEndAt: string;
  };
  commonInfo: {
    lastModifiedAt: string;
    lastModifiedBy: string;
  };
  id: string;
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface InUpdateMember {
  userType: string;
  role: string[];
  privileges: KeyValue[];
  contractStatus: string;
}

export namespace AccountManagementPrivileges {
  export namespace CREATE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "CREATE";
  }
  export namespace READ_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "READ";
  }
  export namespace UPDATE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "UPDATE";
  }
  export namespace DELETE_ACCOUNT {
    export const key = "ACCOUNT";
    export const value = "DELETE";
  }
}

export namespace UserManagementPrivileges {
  export namespace CREATE_USER {
    export const key = "USER";
    export const value = "CREATE";
  }
  export namespace READ_USER {
    export const key = "USER";
    export const value = "READ";
  }
  export namespace UPDATE_USER {
    export const key = "USER";
    export const value = "UPDATE";
  }
  export namespace DELETE_USER {
    export const key = "USER";
    export const value = "DELETE";
  }
}

export namespace MeasurementPrivileges {
  export namespace READ_USER {
    export const key = "USER";
    export const value = "READ";
  }

  export namespace CREATE_RECORD {
    export const key = "RECORD";
    export const value = "CREATE";
  }
  export namespace READ_RECORD {
    export const key = "RECORD";
    export const value = "READ";
  }
  export namespace UPDATE_RECORD {
    export const key = "RECORD";
    export const value = "UPDATE";
  }
  export namespace DELETE_RECORD {
    export const key = "RECORD";
    export const value = "DELETE";
  }

  export namespace CREATE_FOLLOWUP {
    export const key = "FOLLOW-UP";
    export const value = "CREATE";
  }
  export namespace READ_FOLLOWUP {
    export const key = "FOLLOW-UP";
    export const value = "READ";
  }
  export namespace UPDATE_FOLLOWUP {
    export const key = "FOLLOW-UP";
    export const value = "UPDATE";
  }
  export namespace DELETE_FOLLOWUP {
    export const key = "FOLLOW-UP";
    export const value = "DELETE";
  }
}

export namespace ReferePrivileges {
  export namespace READ_USER {
    export const key = "USER";
    export const value = "READ";
  }

  export namespace READ_RECORD {
    export const key = "RECORD";
    export const value = "READ";
  }

  export namespace READ_FOLLOWUP {
    export const key = "FOLLOW-UP";
    export const value = "READ";
  }
}
